export interface DashboardInputs {
  price: number
  productCost: number
  fleteInicial: number
  cancelledPct: number // % pedidos cancelados (never leave warehouse)
  returnPct: number // % devoluciones sobre despachados
  cpaIdeal: number
}

export interface DashboardMetrics {
  cpaIdeal: number
  cpaCosteado: number
  fleteReal: number
  grossMargin: number
  netProfit: number
  netMargin: number
  cpaBreakEven: number
  roasBreakEven: number
  roasTarget: number
  // Funnel
  funnelOrders: number
  funnelDispatched: number
  funnelDelivered: number
  dispatchedPct: number
  efficiencyFinal: number
  cancelledPct: number
  returnPct: number
  trafficLightState: 'green' | 'yellow' | 'red'
}

export type CountryCode = 'CO' | 'MX' | 'EC' | 'AR'

export interface CountryConfig {
  code: CountryCode
  name: string
  currency: string
  symbol: string
  locale: string
  flag: string
}

export const COUNTRIES: CountryConfig[] = [
  {
    code: 'CO',
    name: 'Colombia',
    currency: 'COP',
    symbol: 'COP $',
    locale: 'es-CO',
    flag: '🇨🇴',
  },
  {
    code: 'MX',
    name: 'México',
    currency: 'MXN',
    symbol: 'MXN $',
    locale: 'es-MX',
    flag: '🇲🇽',
  },
  {
    code: 'EC',
    name: 'Ecuador',
    currency: 'USD',
    symbol: 'USD $',
    locale: 'en-US',
    flag: '🇪🇨',
  },
  {
    code: 'AR',
    name: 'Argentina',
    currency: 'ARS',
    symbol: 'ARS $',
    locale: 'es-AR',
    flag: '🇦🇷',
  },
]

export function getCountry(code: CountryCode): CountryConfig {
  return COUNTRIES.find((c) => c.code === code)!
}

export function calculateMetrics(inputs: DashboardInputs): DashboardMetrics {
  const {
    price,
    productCost,
    fleteInicial,
    cancelledPct,
    returnPct,
    cpaIdeal,
  } = inputs

  // FUNNEL: COD Funnel Logic
  // Step 1: Dispatched = 100% - Cancelled%
  const dispatchedPct = 100 - cancelledPct
  // Step 2: Efficiency Final = Dispatched% * (1 - Returns%/100)
  const efficiencyFinal = dispatchedPct * (1 - returnPct / 100)
  const efficiencyDecimal = efficiencyFinal / 100

  // Funnel numbers (base 100 orders)
  const funnelOrders = 100
  const funnelDispatched = Math.round(dispatchedPct)
  const funnelDelivered = Math.round(efficiencyFinal)

  // STEP 0: Flete Real = Flete Inicial / Eficiencia Final
  const fleteReal = efficiencyDecimal > 0 ? fleteInicial / efficiencyDecimal : 0

  // STEP 1: Gross Margin (using Flete Real)
  const grossMargin = price - productCost - fleteReal

  // STEP 2: CPA Break-Even
  const cpaBreakEvenExact = grossMargin * efficiencyDecimal

  // STEP 3: ROAS Break-Even = Price / CPA Break-Even
  const roasBreakEvenExact =
    cpaBreakEvenExact > 0 ? price / cpaBreakEvenExact : 0

  // STEP 3b: ROAS Target = Price / CPA Ideal
  const roasTargetExact = cpaIdeal > 0 ? price / cpaIdeal : 0

  // STEP 3c: CPA Costeado = CPA Ideal / Eficiencia Final
  const cpaCosteadoExact =
    efficiencyDecimal > 0 ? cpaIdeal / efficiencyDecimal : 0

  // STEP 4: Net Profit per delivered order
  const netProfit = price - productCost - fleteReal - cpaCosteadoExact

  // STEP 5: Net Margin
  const netMargin = price > 0 ? (netProfit / price) * 100 : 0

  // Traffic Light State
  let trafficLightState: 'green' | 'yellow' | 'red'
  if (cpaIdeal < cpaBreakEvenExact * 0.6) {
    trafficLightState = 'green'
  } else if (cpaIdeal < cpaBreakEvenExact) {
    trafficLightState = 'yellow'
  } else {
    trafficLightState = 'red'
  }

  const cpaBreakEvenRounded = Math.round(cpaBreakEvenExact)
  const roasBreakEvenRounded = parseFloat(roasBreakEvenExact.toFixed(2))

  return {
    cpaIdeal,
    cpaCosteado: Math.round(cpaCosteadoExact),
    fleteReal: Math.round(fleteReal),
    grossMargin: Math.round(grossMargin),
    netProfit: Math.round(netProfit),
    netMargin: parseFloat(netMargin.toFixed(2)),
    cpaBreakEven: cpaBreakEvenRounded,
    roasBreakEven: roasBreakEvenRounded,
    roasTarget: parseFloat(roasTargetExact.toFixed(2)),
    funnelOrders,
    funnelDispatched,
    funnelDelivered,
    dispatchedPct: parseFloat(dispatchedPct.toFixed(1)),
    efficiencyFinal: parseFloat(efficiencyFinal.toFixed(1)),
    cancelledPct,
    returnPct,
    trafficLightState,
  }
}

export function formatCurrency(
  value: number,
  countryCode: CountryCode = 'CO',
): string {
  const country = getCountry(countryCode)
  return country.symbol + Math.abs(value).toLocaleString(country.locale)
}

export function formatNumber(
  value: number,
  countryCode: CountryCode = 'CO',
): string {
  const country = getCountry(countryCode)
  return Math.abs(value).toLocaleString(country.locale)
}
